import * as React from 'react';
import { pb } from '@/lib/pb';
import { COLLECTIONS, type AppUser, type UserRole } from '@/lib/types';
import { Btn, Icon } from '@/ui';
import { useAuth } from './AuthContext';
import { PageHeader, Card, Spinner, Modal, TextField, SelectField, Checkbox, useToast, pbError } from './ui';

const ROLE_OPTIONS = [
  { value: 'admin', label: 'Admin (Vollzugriff)' },
  { value: 'moderator', label: 'Editor (nur Blog)' },
  { value: 'user', label: 'Benutzer (kein Admin-Zugang)' },
];

function roleLabel(r: string): string {
  return ROLE_OPTIONS.find((o) => o.value === r)?.label ?? r;
}

interface FormState { email: string; name: string; role: UserRole; password: string; banned: boolean; }
const emptyForm: FormState = { email: '', name: '', role: 'moderator', password: '', banned: false };

export function Users() {
  const { user: me } = useAuth();
  const toast = useToast();
  const [list, setList] = React.useState<AppUser[] | null>(null);
  const [editing, setEditing] = React.useState<AppUser | null>(null);
  const [creating, setCreating] = React.useState(false);
  const [form, setForm] = React.useState<FormState>(emptyForm);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState('');

  const reload = React.useCallback(async () => {
    try {
      const items = await pb.collection(COLLECTIONS.users).getFullList<AppUser>({ sort: '-created' });
      setList(items);
    } catch (e) {
      toast.show(pbError(e), 'error');
      setList([]);
    }
  }, [toast]);

  React.useEffect(() => { void reload(); }, [reload]);

  const openCreate = () => { setForm(emptyForm); setError(''); setCreating(true); };
  const openEdit = (u: AppUser) => {
    setForm({ email: u.email, name: u.name, role: u.role, password: '', banned: u.banned });
    setError('');
    setEditing(u);
  };
  const closeModal = () => { setCreating(false); setEditing(null); };

  const saveCreate = async () => {
    setSaving(true); setError('');
    try {
      // createRule forbids setting role at create → create without role, then patch it.
      const rec = await pb.collection(COLLECTIONS.users).create<AppUser>({
        email: form.email.trim(), password: form.password, passwordConfirm: form.password,
        name: form.name.trim(), emailVisibility: false, verified: true,
      });
      await pb.collection(COLLECTIONS.users).update(rec.id, { role: form.role, banned: false });
      toast.show('Benutzer angelegt.', 'success');
      closeModal();
      await reload();
    } catch (e) {
      setError(pbError(e));
    } finally {
      setSaving(false);
    }
  };

  const saveEdit = async () => {
    if (!editing) return;
    setSaving(true); setError('');
    try {
      const patch: Record<string, unknown> = { name: form.name.trim(), role: form.role, banned: form.banned };
      if (form.password) { patch.password = form.password; patch.passwordConfirm = form.password; }
      await pb.collection(COLLECTIONS.users).update(editing.id, patch);
      toast.show('Benutzer aktualisiert.', 'success');
      closeModal();
      await reload();
    } catch (e) {
      setError(pbError(e));
    } finally {
      setSaving(false);
    }
  };

  const toggleBanned = async (u: AppUser) => {
    try {
      await pb.collection(COLLECTIONS.users).update(u.id, { banned: !u.banned });
      toast.show(u.banned ? 'Benutzer aktiviert.' : 'Benutzer deaktiviert.', 'success');
      await reload();
    } catch (e) {
      toast.show(pbError(e), 'error');
    }
  };

  const remove = async (u: AppUser) => {
    if (!confirm(`Benutzer „${u.name || u.email}" wirklich löschen?`)) return;
    try {
      await pb.collection(COLLECTIONS.users).delete(u.id);
      toast.show('Benutzer gelöscht.', 'success');
      await reload();
    } catch (e) {
      toast.show(pbError(e), 'error');
    }
  };

  return (
    <div>
      <PageHeader
        title="Benutzer"
        subtitle="Zugänge zum Admin-Bereich verwalten."
        actions={<Btn variant="primary" size="sm" iconLeft="plus" onClick={openCreate}>Neuer Benutzer</Btn>}
      />

      <div style={{ fontSize: 12.5, color: 'var(--text-200)', marginBottom: 16, display: 'flex', gap: 8, alignItems: 'center' }}>
        <Icon name="info" size={15} /> Diese Benutzer teilen sich den zentralen Zugangs-Pool der Instanz.
      </div>

      <Card style={{ padding: 0, overflow: 'hidden' }}>
        {list === null ? <Spinner /> : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ textAlign: 'left', color: 'var(--text-200)', fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                <th style={{ padding: '12px 18px' }}>Name</th>
                <th style={{ padding: '12px 18px' }}>E-Mail</th>
                <th style={{ padding: '12px 18px' }}>Rolle</th>
                <th style={{ padding: '12px 18px' }}>Status</th>
                <th style={{ padding: '12px 18px', textAlign: 'right' }}>Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {list.map((u) => {
                const isMe = me?.id === u.id;
                return (
                  <tr key={u.id} style={{ borderTop: '1px solid var(--border-subtle)', color: 'var(--text-100)' }}>
                    <td style={{ padding: '12px 18px', fontWeight: 500 }}>{u.name || '—'}{isMe && <span style={{ color: 'var(--text-200)', fontWeight: 400 }}> (Sie)</span>}</td>
                    <td style={{ padding: '12px 18px', color: 'var(--text-200)' }}>{u.email}</td>
                    <td style={{ padding: '12px 18px' }}>{roleLabel(u.role)}</td>
                    <td style={{ padding: '12px 18px' }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: u.banned ? '#e5484d' : '#30a46c' }}>
                        <span style={{ width: 7, height: 7, borderRadius: '50%', background: u.banned ? '#e5484d' : '#30a46c' }} />
                        {u.banned ? 'Deaktiviert' : 'Aktiv'}
                      </span>
                    </td>
                    <td style={{ padding: '10px 18px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <IconBtn icon="pencil" title="Bearbeiten" onClick={() => openEdit(u)} />
                      <IconBtn icon={u.banned ? 'user-check' : 'user-x'} title={u.banned ? 'Aktivieren' : 'Deaktivieren'} disabled={isMe} onClick={() => toggleBanned(u)} />
                      <IconBtn icon="trash-2" title="Löschen" disabled={isMe} danger onClick={() => remove(u)} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </Card>

      {(creating || editing) && (
        <Modal
          title={creating ? 'Neuer Benutzer' : 'Benutzer bearbeiten'}
          onClose={closeModal}
          footer={<>
            <Btn variant="secondary" size="sm" onClick={closeModal}>Abbrechen</Btn>
            <Btn variant="primary" size="sm" onClick={creating ? saveCreate : saveEdit}>{saving ? 'Speichern…' : 'Speichern'}</Btn>
          </>}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <TextField label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} placeholder="Vor- und Nachname" />
            <TextField label="E-Mail" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} required disabled={!creating} hint={!creating ? 'E-Mail kann hier nicht geändert werden.' : undefined} />
            <SelectField label="Rolle" value={form.role} onChange={(v) => setForm({ ...form, role: v as UserRole })} options={ROLE_OPTIONS} />
            <TextField
              label={creating ? 'Passwort' : 'Neues Passwort'}
              type="password"
              value={form.password}
              onChange={(v) => setForm({ ...form, password: v })}
              required={creating}
              hint={creating ? 'Mindestens 8 Zeichen.' : 'Leer lassen, um das Passwort nicht zu ändern.'}
            />
            {!creating && (
              <Checkbox label="Deaktiviert (kein Login möglich)" checked={form.banned} onChange={(v) => setForm({ ...form, banned: v })} />
            )}
            {error && <div style={{ fontSize: 13, color: '#e5484d' }}>{error}</div>}
          </div>
        </Modal>
      )}
    </div>
  );
}

function IconBtn({ icon, title, onClick, disabled, danger }: { icon: string; title: string; onClick: () => void; disabled?: boolean; danger?: boolean }) {
  return (
    <button
      title={title} aria-label={title} onClick={onClick} disabled={disabled}
      style={{ background: 'none', border: 'none', cursor: disabled ? 'default' : 'pointer', padding: 7, marginLeft: 2, borderRadius: 'var(--radius-sm)', color: disabled ? 'var(--border-default)' : danger ? '#e5484d' : 'var(--text-200)', display: 'inline-flex', verticalAlign: 'middle' }}
    >
      <Icon name={icon} size={17} />
    </button>
  );
}
